import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Spinner } from '@ixicare/ui';
import { ProfileType } from '@ixicare/resources';
import connect from '../../../store/connect';
import DevicesStore from '~modules/devices/store/Devices';
import DeviceStore from '~modules/devices/store/Device';
import { t } from '~i18n';
import AuthStore from '~store/Auth';
import PermissionsStore from '~store/Permissions';
import { FieldValues, Role } from '../components/DeviceConfigForm/schema';
import DeviceConfigForm from '../components/DeviceConfigForm/DeviceConfigForm';
import { GatingContext } from '../components/DeviceConfigForm/utils/gating';
import { useSiteConfigValues } from '../hooks/useSiteConfigValues';
import { useOwnerLayerValues } from '../hooks/useOwnerLayerValues';
import { State } from '~store/types';
import ErrorPage from '~/pages/ErrorPage';
import { useFollowSite } from '~hooks/useSelectedSite';
import { useStores } from '~store/utils';

interface Props {
  devicesStore: DevicesStore;
  deviceStore: DeviceStore;
  authStore: AuthStore;
  permissions: PermissionsStore;
}

const profileTypeToRole = (pt: ProfileType | null | undefined): Role => {
  switch (pt) {
    case ProfileType.WatcherrEmployee:
      return 'WATCHERR_EMPLOYEE';
    case ProfileType.Installer:
      return 'INSTALLER';
    default:
      return 'INSTALLER';
  }
};

const DeviceConfigContainer: React.FC<Props> = ({ devicesStore, deviceStore, authStore, permissions }) => {
  const { uuid } = useParams<{ uuid: string }>();
  const siteConfigValues = useSiteConfigValues(deviceStore.deviceType, deviceStore.siteId);
  const { sites } = useStores();

  // siteId still belongs to the previous device until this one has loaded.
  useFollowSite(
    deviceStore.uuid === uuid && !deviceStore.loading
      ? sites.sites.find((s) => s.id === deviceStore.siteId)?.uuid
      : undefined
  );
  const ownerLayerValues = useOwnerLayerValues(uuid);

  // Memoised: the form's layer derivations key off this object, so a fresh literal each render
  // would rebuild them and re-render every section.
  const configLayers = React.useMemo(
    () => ({ device: deviceStore.deviceOverrides, owner: ownerLayerValues, site: siteConfigValues }),
    [deviceStore.deviceOverrides, ownerLayerValues, siteConfigValues]
  );

  React.useEffect(() => {
    if (!uuid) return;
    // The schema is per device type and firmware, so it only goes stale when the device does.
    if (deviceStore.uuid !== uuid) devicesStore.reset();
    deviceStore.loadDevice(uuid);
    deviceStore.loadDeviceSettings(uuid);
  }, [uuid, deviceStore, devicesStore]);

  React.useEffect(() => {
    // The variant follows the caller's profile server-side; userType is only a proxy for the
    // session being resolved, so the request is not made before we are authenticated.
    if (!authStore.userType) return;
    if (deviceStore.uuid !== uuid || !deviceStore.deviceType) return;
    devicesStore.loadConfigSchema(deviceStore.deviceType, deviceStore.swVersion ?? undefined);
  }, [
    uuid,
    authStore.userType,
    deviceStore,
    deviceStore.uuid,
    deviceStore.deviceType,
    deviceStore.swVersion,
    devicesStore,
  ]);

  // The device configuration form writes device settings, which the API now takes
  // `can_edit_all_devices` for. Hidden rather than left to fail: an admin carer can reach this page
  // through a link but has no device permissions, and a form that only refuses on save is worse
  // than one that is not offered.
  if (!permissions.has('can_edit_all_devices')) {
    return <ErrorPage statusCode={404} />;
  }

  if (devicesStore.schemaLoading || !devicesStore.schema) {
    if (devicesStore.schemaError) {
      return (
        <Alert type="error">
          {t('device-config.error.load', {
            message: 'Could not load device configuration.',
          })}
        </Alert>
      );
    }
    return <Spinner />;
  }

  if (deviceStore.loadingSettings || deviceStore.loading || deviceStore.uuid !== uuid || !deviceStore.macAddress) {
    return <Spinner />;
  }

  const gating: GatingContext = {
    role: profileTypeToRole(authStore.userType),
    permissions: permissions.names,
  };

  const onSubmit = (payload: FieldValues) => deviceStore.saveConfig(payload);
  const isInstaller = authStore.userType === ProfileType.Installer;

  return (
    <DeviceConfigForm
      schema={devicesStore.schema}
      title={t('device-config.page.title')}
      subtitle={t(devicesStore.schema.meta.subtitle_key)}
      configValues={{ ...deviceStore.configValues, phone_number: deviceStore.phoneNumber ?? '' }}
      scope={{ kind: 'device', mac: deviceStore.macAddress ?? '', imei: deviceStore.imeiNumber ?? undefined }}
      isInstaller={isInstaller}
      gating={gating}
      onSubmit={onSubmit}
      onResetOverrides={deviceStore.resetDeviceOverrides}
      ownLayer="device"
      configLayers={configLayers}
    />
  );
};

export default connect((state: State) => ({
  devicesStore: state.devices,
  deviceStore: state.device,
  authStore: state.auth,
  permissions: state.permissions,
}))(DeviceConfigContainer);
